import type { InstitutionalHolderRecord, InstitutionalHoldersFileResponse } from '@/services/api';
import {
  buildChartInstitutionsForQuarter,
  buildChartOwnershipInstitutionsForQuarter,
  formatInstitutionalChartShares,
  formatInstitutionalHolderDisplayName,
  holderPositionKey,
  institutionalHolderCardMetricLabel,
  type FilingPeriodCard,
} from '@/utils/institutionalHoldersChartData';
import { quarterKeyToLabel } from '@/utils/institutionalHolderQuarterHistory';
import type { InstitutionalHoldersChartDisplayMode } from '@/utils/institutionalHoldersChartDisplayMode';

export interface InstitutionalHolderSearchResult {
  positionKey: string;
  quarterKey: string;
  quarterLabel: string;
  displayName: string;
  shortName: string;
  metricLabel: string;
  /** Quarter column is currently on the price chart. */
  onChart: boolean;
}

function parseQuarterSortKey(key: string): number {
  const m = key.match(/^(\d{4})-Q([1-4])$/);
  if (!m) return 0;
  return parseInt(m[1]!, 10) * 100 + parseInt(m[2]!, 10);
}

function sortQuarterKeysDesc(keys: string[]): string[] {
  return [...keys].sort((a, b) => parseQuarterSortKey(b) - parseQuarterSortKey(a));
}

function holderMatchesQuery(holder: InstitutionalHolderRecord, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const name = (holder.investorName ?? '').toLowerCase();
  if (name.includes(q)) return true;
  const short = formatInstitutionalHolderDisplayName(holder).toLowerCase();
  if (short.includes(q)) return true;
  const cikDigits = (holder.cik ?? '').replace(/\D/g, '');
  const qDigits = q.replace(/\D/g, '');
  if (qDigits.length >= 4 && cikDigits.includes(qDigits)) return true;
  return false;
}

function cardForHolderInQuarter(
  holder: InstitutionalHolderRecord,
  displayMode: InstitutionalHoldersChartDisplayMode,
  quarterHolders: InstitutionalHolderRecord[]
): FilingPeriodCard | null {
  const positionKey = holderPositionKey(holder);
  const cards =
    displayMode === 'ownership'
      ? buildChartOwnershipInstitutionsForQuarter(quarterHolders)
      : buildChartInstitutionsForQuarter(quarterHolders);
  return cards.find((c) => c.positionKey === positionKey) ?? null;
}

function fallbackMetric(holder: InstitutionalHolderRecord): string {
  const ownership = holder.ownership ?? 0;
  if (ownership > 0) return `${ownership.toFixed(2)}%`;
  const shares = holder.sharesNumber ?? 0;
  if (shares > 0) return formatInstitutionalChartShares(shares);
  return '—';
}

/**
 * Search all 13F quarters in the payload (not only chart-visible columns).
 */
export function searchInstitutionalHoldersAcrossQuarters(
  payload: InstitutionalHoldersFileResponse | null | undefined,
  query: string,
  displayMode: InstitutionalHoldersChartDisplayMode,
  chartQuarterKeys: ReadonlySet<string>,
  maxResults = 48
): InstitutionalHolderSearchResult[] {
  const q = query.trim();
  if (!q || !payload?.quarters) return [];

  const results: InstitutionalHolderSearchResult[] = [];
  const keys = sortQuarterKeysDesc(
    Object.keys(payload.quarters).filter((k) => (payload.quarters![k]?.holders?.length ?? 0) > 0)
  );

  for (const quarterKey of keys) {
    const holders = payload.quarters[quarterKey]?.holders ?? [];
    for (const holder of holders) {
      const shares = holder.sharesNumber ?? 0;
      if (shares <= 0 && !holder.isSoldOut) continue;
      if (!holderMatchesQuery(holder, q)) continue;

      const positionKey = holderPositionKey(holder);
      const displayName = (holder.investorName ?? '').trim() || formatInstitutionalHolderDisplayName(holder);
      const shortName = formatInstitutionalHolderDisplayName(holder);
      const card = cardForHolderInQuarter(holder, displayMode, holders);
      const metricLabel = card
        ? institutionalHolderCardMetricLabel(card, displayMode)
        : fallbackMetric(holder);

      results.push({
        positionKey,
        quarterKey,
        quarterLabel: quarterKeyToLabel(quarterKey),
        displayName,
        shortName,
        metricLabel,
        onChart: chartQuarterKeys.has(quarterKey),
      });

      if (results.length >= maxResults) return results;
    }
  }

  return results;
}

/**
 * Filter institutions within each quarter column. Keeps every period in the array
 * so chart column X positions stay aligned with quarter-end dividers.
 */
export function filterFilingPeriodsBySearch<T extends { institutions: { fullName: string; name: string }[] }>(
  periods: T[],
  query: string
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return periods;
  return periods.map((p) => ({
    ...p,
    institutions: p.institutions.filter(
      (i) => i.fullName.toLowerCase().includes(q) || i.name.toLowerCase().includes(q)
    ),
  }));
}

export function filingPeriodsHaveSearchMatch<T extends { institutions: unknown[] }>(
  periods: T[]
): boolean {
  return periods.some((p) => p.institutions.length > 0);
}

/** Stable attribute for scrolling to a ribbon card. */
export function institutionalHolderRibbonSelector(quarterKey: string, positionKey: string): string {
  return `[data-inst-holder="${quarterKey}|${positionKey}"]`;
}
